/**
 * Validation report side card.
 * Plain JSX — outside <Canvas>.
 *
 * v1.18 — real-time clash warnings appended (with per-warning "Ignore"
 * toggle that flips `clash_ignored` on the offending equipment-list item).
 */
import { CheckCircle, WarningCircle, XCircle, EyeSlash, Eye, MagicWand } from "@phosphor-icons/react";

export default function ValidationReport({ report, clashes = [], onToggleIgnore, onSnapToCompliance, onSnapAll }) {
  const issuesFromServer = (report?.issues || []);
  const activeClashes = clashes.filter((c) => !c.ignored);
  const ignoredClashes = clashes.filter((c) => c.ignored);

  const totalErrors = (report?.summary?.errors || 0);
  const totalWarnings = (report?.summary?.warnings || 0) + activeClashes.length;
  const overallCompliant = report
    ? (report.compliant && activeClashes.length === 0)
    : activeClashes.length === 0;

  return (
    <aside className="border border-white/10 bg-[#0a0d12] p-4 font-mono text-sm" data-testid="r3d-report">
      <div className="label-tag mb-3">// VALIDATION REPORT</div>
      <div
        className={`text-xs uppercase tracking-[0.2em] mb-3 font-bold ${overallCompliant ? "text-[#00E5FF]" : "text-[#FF3B5C]"}`}
        data-testid="r3d-report-status"
      >
        {overallCompliant
          ? <span className="inline-flex items-center gap-1"><CheckCircle weight="fill" size={14} /> COMPLIANT</span>
          : <span className="inline-flex items-center gap-1"><XCircle weight="fill" size={14} /> NON-COMPLIANT</span>}
      </div>

      {report ? (
        <div className="grid grid-cols-2 gap-2 text-xs mb-3">
          <div className="border border-white/10 p-2"><div className="text-gray-500">Placed</div><div className="text-white font-display text-lg">{report.summary.items_placed}</div></div>
          <div className="border border-white/10 p-2"><div className="text-gray-500">Unplaced</div><div className="text-white font-display text-lg">{report.summary.items_unplaced}</div></div>
          <div className="border border-red-500/30 p-2"><div className="text-red-300">Errors</div><div className="text-red-300 font-display text-lg">{totalErrors}</div></div>
          <div className="border border-yellow-500/30 p-2"><div className="text-yellow-300">Warnings</div><div className="text-yellow-300 font-display text-lg">{totalWarnings}</div></div>
        </div>
      ) : (
        <div className="text-gray-500 text-xs mb-3">
          Click <span className="text-[#00E5FF]">Validate Design</span> to run the full NEC 110.26 check. Real-time
          clash monitoring below.
        </div>
      )}

      {/* Real-time clashes — separate section so they're visible without running full Validate */}
      {(activeClashes.length > 0 || ignoredClashes.length > 0) && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] text-gray-500 uppercase tracking-[0.2em]">Clash monitor · real-time</div>
            {/* v1.63 — Auto-resolve-all only available when there's at least
                one resolvable (CLEARANCE or OVERLAP w/ partner) active clash. */}
            {onSnapAll && activeClashes.some((c) => c.partner_id && (c.code === "CLEARANCE" || c.code === "OVERLAP")) && (
              <button
                onClick={onSnapAll}
                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-[#00E5FF] border border-[#00E5FF]/40 hover:bg-[#00E5FF]/10"
                title="Sequentially snap every resolvable clash to NEC 110.26 36-inch clearance"
                data-testid="r3d-clash-snap-all"
              >
                <MagicWand size={11} weight="fill" /> Resolve all
              </button>
            )}
          </div>
          <ul className="space-y-2 text-xs" data-testid="r3d-clashes">
            {activeClashes.map((c) => (
              <li key={c.id} className="border-l-2 border-[#FFCC00] pl-2 text-yellow-200">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-1">
                      <WarningCircle size={12} weight="fill" /><span className="font-bold">{c.code}</span>
                    </div>
                    <div>{c.message}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {/* v1.62 — "Snap into compliance" only shows for resolvable
                        clashes (has partner_id + axis for CLEARANCE, or partner_id
                        for OVERLAP — v1.63 added OVERLAP snap). One-click
                        nudge to satisfy NEC 110.26(A) 36" rule. */}
                    {onSnapToCompliance &&
                      c.partner_id &&
                      (c.code === "CLEARANCE" ? c.axis : c.code === "OVERLAP") && (
                      <button
                        onClick={() => onSnapToCompliance(c)}
                        className="text-[10px] text-[#00E5FF] hover:text-white inline-flex items-center gap-1"
                        title={`Auto-nudge to reach NEC 36" clearance`}
                        data-testid={`r3d-clash-snap-${c.item_id}`}
                      >
                        <MagicWand size={11} weight="fill" /> Snap
                      </button>
                    )}
                    {onToggleIgnore && (
                      <button
                        onClick={() => onToggleIgnore(c.item_id, true)}
                        className="text-[10px] text-gray-400 hover:text-white inline-flex items-center gap-1"
                        data-testid={`r3d-clash-ignore-${c.item_id}`}
                      >
                        <EyeSlash size={11} /> Ignore
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
            {ignoredClashes.length > 0 && (
              <li className="border-l-2 border-white/10 pl-2 text-gray-400">
                <div className="flex items-center justify-between gap-2">
                  <span>{ignoredClashes.length} ignored warning{ignoredClashes.length === 1 ? "" : "s"}</span>
                  {onToggleIgnore && (
                    <button
                      onClick={() => ignoredClashes.forEach((c) => onToggleIgnore(c.item_id, false))}
                      className="text-[10px] text-gray-400 hover:text-white inline-flex items-center gap-1"
                      data-testid="r3d-clash-restore-all"
                    >
                      <Eye size={11} /> Restore
                    </button>
                  )}
                </div>
              </li>
            )}
          </ul>
        </div>
      )}

      {report && (
        <>
          <div className="text-[10px] text-gray-500 mb-2 uppercase tracking-[0.2em]">Layout issues</div>
          <ul className="space-y-2 text-xs max-h-72 overflow-auto pr-1" data-testid="r3d-issues">
            {issuesFromServer.map((i, idx) => (
              <li key={`${i.code || "issue"}-${i.item_id || idx}`}
                  className={`border-l-2 pl-2 ${i.level === "error" ? "border-[#FF3B5C] text-red-200"
                            : i.level === "warning" ? "border-[#FFCC00] text-yellow-200"
                            : "border-[#3C82B4] text-cyan-200"}`}>
                <div className="flex items-center gap-1">
                  {i.level === "error" ? <XCircle size={12} weight="fill" />
                    : i.level === "warning" ? <WarningCircle size={12} weight="fill" />
                    : <CheckCircle size={12} />}
                  <span className="font-bold">{i.code}</span>
                </div>
                <div>{i.message}</div>
              </li>
            ))}
            {issuesFromServer.length === 0 && (
              <li className="text-gray-500">No layout issues — design complies with NEC 110.26.</li>
            )}
          </ul>
          <div className="text-[10px] text-gray-500 mt-3 leading-relaxed">
            Reference: {report.metadata.code_reference}<br />
            Working space: ≥ {report.metadata.working_space_in}"<br />
            Headroom: ≥ {report.metadata.headroom_required_in}"
          </div>
        </>
      )}
    </aside>
  );
}
